import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import _ from 'lodash';
import { useLogger } from 'src/appSelectorContext';
import { v4 as uuidv4 } from 'uuid';

import { UNKNOWN_MANUFACTURER_PLACEHOLDER } from '../../constants';
import { Context, Types } from '../../store';
import {
  MetricPair,
  MetricSeverity,
  MetricType,
  Model,
  MPNToIPNMapping,
  MPNToIPNPair,
  ReceiveType,
  ReferenceQueryResponseItem,
  URIParams,
  ValidationStatusType,
} from '../../store/types';
import { useService } from '../../store/useService';
import {
  convertMpnMappingToReferenceLineItem,
  filterDuplicateLineItemsByReferenceLineItemId,
  filterOutErrorAndPairingInvalidLineItems,
  getCommonLineItemAndIpnLists,
  getManufacturerToAssociatedModelsMapping,
  isDashrollMatch,
  sortByIPN,
  sortByReferenceItem,
} from './DashrollUtilities';
import { SEARCH_MODELS } from './MPNsToReceiveService';
import { MODEL_ERROR_CODES, BLOCKING_ERRORS } from './constants';

interface props {
  onCompleted?: () => void;
  siteName: string;
  orderType: string;
  receiveReason: string;
  mpnInput: string;
}

interface ReferenceLineItemResponse {
  associatedIPNs: MPNToIPNPair[];
  referenceLineItems: any[];
  paginationNextTokens: {
    mpnToIpnMappingToken?: string | null;
    referenceLineItemsToken?: string | null;
  };
}

const INIT_REFERENCE_LINE_ITEMS_RESPONSE: ReferenceLineItemResponse = {
  associatedIPNs: [],
  referenceLineItems: [],
  paginationNextTokens: {},
};

/**
 * The useModelsToReceive hook provides data and business logic for the ModelsToReceive component
 *
 */
export const useMPNsToReceive = ({ onCompleted, siteName, orderType, receiveReason, mpnInput }: props) => {
  const history = useHistory();
  const { state, dispatch }: any = useContext(Context);
  const logger = useLogger();
  const { modelId, referenceId, receiveType } = useParams<URIParams>();
  const rma = receiveType === 'no_reference';
  const { useLazyQuery } = useService();
  const isAdjustment = receiveType === ReceiveType.INVENTORY_ADJUSTMENT;
  const skipReferenceId = rma || isAdjustment;
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<boolean>(false);
  const [asns, setAsns] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<Model>({});
  const [mpnToIPNMapping, setMPNToIPNMapping] = useState<MPNToIPNMapping | undefined>();
  const paginatedResponse = useRef<ReferenceLineItemResponse>(INIT_REFERENCE_LINE_ITEMS_RESPONSE);
  const NULL_STRING: string = 'null';

  useEffect(() => {
    setSelectedModel({});
  }, [mpnInput]);

  const [searchModels, searchModelsResponse] = useLazyQuery(SEARCH_MODELS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      try {
        // variable Context is set to null with HTTP Query, while in execution context
        let variableArgs = searchModelsResponse.variables;
        const mpn = variableArgs?.mpn || data?.listReferenceLineItems?.mpnToIpnMapping?.mpn;
        let selectedModel: Model = {
          modelNumber: mpn,
          expectedMPN: mpn,
          referenceId,
          description: '',
          rmaLineItemNumber: '',
          siteName,
          requisitionId: '',
          requisitionLineItemNumber: '',
          wwFaId: '',
          wwFaLineItemNumber: '',
          mappedIPN: null,
          mpnIpnId: undefined,
          metricsList: [],
          referenceLineItemStatus: undefined,
          tags: [],
          receiveReason: '',
          mappedOrderNumber: '',
          mappedOrderLineItemNumber: '',
        };

        let updatedAssociatedIpns = _.uniqWith(
          [
            ...paginatedResponse.current.associatedIPNs,
            ...(data?.listReferenceLineItems?.mpnToIpnMapping?.associatedIpns || []),
          ],
          _.isEqual,
        );

        let updatedReferenceLineItems = _.uniqWith(
          [
            ...paginatedResponse.current.referenceLineItems,
            ...(data?.listReferenceLineItems?.referenceLineItems || []),
          ],
          _.isEqual,
        );

        // check if we need to query additional pages of data
        const tokens = data?.listReferenceLineItems?.paginationNextTokens;

        // TODO: Account for this case in the backend, specifically when Townsend returns <Townsend>null token.
        const updatedPaginationTokens = {
          mpnToIpnMappingToken: tokens?.mpnToIpnMappingToken,
          referenceLineItemsToken: tokens?.referenceLineItemsToken,
        };

        if (tokens?.mpnToIpnMappingToken?.includes(NULL_STRING)) {
          updatedPaginationTokens.mpnToIpnMappingToken = null;
        }

        if (updatedPaginationTokens?.mpnToIpnMappingToken || updatedPaginationTokens?.referenceLineItemsToken) {
          paginatedResponse.current = {
            associatedIPNs: updatedAssociatedIpns,
            referenceLineItems: updatedReferenceLineItems,
            paginationNextTokens: updatedPaginationTokens,
          };
          // pagination tokens are present, recursively invoke the searchModels with updated token.
          searchModels({
            variables: {
              receiveReferenceIdList: variableArgs?.receiveReferenceIdList,
              mpn: variableArgs?.mpn,
              paginationNextTokens: updatedPaginationTokens,
            },
          });
          return;
        } else {
          paginatedResponse.current = INIT_REFERENCE_LINE_ITEMS_RESPONSE;
        }

        const unSortedMpnToIpnMapping: MPNToIPNMapping = {
          mpn: variableArgs?.mpn,
          associatedIpns: _.uniqWith(updatedAssociatedIpns, _.isEqual) || [],
        };
        // check to make sure reference lineitems are passing the correct reference Id
        updatedReferenceLineItems = updatedReferenceLineItems.filter(
          (lineitem) => lineitem.refereceLineItem.referenceId === referenceId,
        );

        const lineitems = _.uniqWith(updatedReferenceLineItems, _.isEqual) || [];
        const sortedLineItems = [...lineitems].sort(sortByReferenceItem);
        const associatedIpns = unSortedMpnToIpnMapping.associatedIpns || [];
        const sortedAssociatedIpns = [...associatedIpns].sort(sortByIPN);
        const mpnToIpnMappingsFromAPI = { ...unSortedMpnToIpnMapping, associatedIpns: sortedAssociatedIpns };

        const referenceLineItems = sortedLineItems?.map((referenceLineItem) => {
          return {
            ...referenceLineItem,
            tagList: referenceLineItem.refereceLineItem.tags,
            referenceLineItemStatus: referenceLineItem.status,
          };
        });
        const references: ReferenceQueryResponseItem[] = state?.receiveReferences || [];
        const reference = references.find(
          (ref: ReferenceQueryResponseItem) => ref.receiveReference.receiveReferenceId === referenceId,
        );

        const isPO =
          reference?.receiveReference.externalReferenceType &&
          [ReceiveType.AWS_PO].includes(reference?.receiveReference.externalReferenceType);

        const errors = referenceLineItems?.filter(
          (ref) =>
            ref?.status?.validationStatusType === ValidationStatusType.ERROR &&
            BLOCKING_ERRORS.includes(ref?.status?.statusCode),
        );
        const error = errors.length > 0 ? errors?.[0] : null;

        if (referenceLineItems.length > 0 && errors.length === referenceLineItems.length) {
          setError('all_line_items_closed');
          setSelectedModel(referenceLineItems);
          return;
        }

        // handle blocking cases for reference and reference line item not active
        if (referenceLineItems.length === 1 && error) {
          setError(error?.status?.statusCode);
          setSelectedModel(referenceLineItems?.[0].refereceLineItem);
          return;
        }

        // ignore any errors
        const referenceLineItemsNoErrors = filterOutErrorAndPairingInvalidLineItems(
          referenceLineItems,
          variableArgs?.mpn,
        );

        const unExpectedLineItems = referenceLineItemsNoErrors?.some(
          (ref) => !ref?.refereceLineItem?.referenceLineItemId,
        );

        const mappedReferenceLineItems = referenceLineItemsNoErrors?.map((ref) => {
          return {
            ...ref?.refereceLineItem,
            tagList: ref?.tagList,
            referenceLineItemStatus: ref?.referenceLineItemStatus,
          };
        });
        const hasReference = skipReferenceId ? true : !!reference;

        // no matching ipns in townsend and ASN
        if (
          mpnToIpnMappingsFromAPI?.associatedIpns?.length === 0 ||
          !mpnToIpnMappingsFromAPI ||
          !hasReference ||
          mpnToIpnMappingsFromAPI?.mpn?.toUpperCase() !== variableArgs?.mpn?.toUpperCase()
        ) {
          setError(MODEL_ERROR_CODES.model_not_exists_catalogue);
          setSelectedModel(selectedModel);
          return;
        }

        const isJanusOrAWSOwned: boolean =
          reference?.receiveReference?.externalReferenceType === ReceiveType.ASN_JANUS ||
          reference?.receiveReference?.externalReferenceType === ReceiveType.ASN_AWS_OWNED_INVENTORY_RO;

        const mpnIPNPairingTownsendList = convertMpnMappingToReferenceLineItem(
          mpnToIpnMappingsFromAPI.associatedIpns,
          mpnToIpnMappingsFromAPI?.mpn,
        );

        // Group entries by manufacturer first, and then process dashrolls for each grouping separately.
        const manufacturerToModelsMapping: Map<string, Model[]> =
          getManufacturerToAssociatedModelsMapping(mpnIPNPairingTownsendList);

        let commonIpnList: any[] = [];
        let commonIpnListNoMatches: any[] = [];
        let commonLineItemList: any[] = [];
        manufacturerToModelsMapping.forEach((modelList, manufacturer) => {
          // Provide a list of IPNs successfully paired/dash-rolled that match ASNs
          // and also provides a list of ASNs that match one of the IPNs
          const {
            ipnList: ipnListForManufacturer,
            lineItemList: commonLineItemListForManufacturer,
            matchingLineItemsFound: matchingLineItemsFoundForManufacturer,
          } = getCommonLineItemAndIpnLists(
            mappedReferenceLineItems,
            modelList,
            manufacturer === UNKNOWN_MANUFACTURER_PLACEHOLDER ? undefined : manufacturer,
          );
          commonLineItemList = commonLineItemList.concat(commonLineItemListForManufacturer);
          commonIpnListNoMatches = commonIpnListNoMatches.concat(ipnListForManufacturer);
          if (matchingLineItemsFoundForManufacturer) {
            commonIpnList = commonIpnList.concat(ipnListForManufacturer);
          }
        });

        commonIpnList = commonIpnList.length > 0 ? commonIpnList : commonIpnListNoMatches;
        if (commonIpnList?.length === 1) {
          selectedModel = {
            ...selectedModel,
            manufacturer: commonIpnList[0]?.manufacturer,
          };
        }
        // Remove any duplicates
        const noduplicateLineItemList = filterDuplicateLineItemsByReferenceLineItemId(commonLineItemList);
        // If there is more than one line item, further reduce IPN/manufacturer pairs to those that are common with the found line items
        if (noduplicateLineItemList.length > 0) {
          const expectedIPNs: string[] = noduplicateLineItemList.map((lineItem) => lineItem.expectedIPN);
          const filteredIpnList = commonIpnList.filter(
            (ipnManufacturerPair) =>
              expectedIPNs.filter((expectedIPN: string) => {
                return ipnManufacturerPair.ipn?.includes(expectedIPN);
              }).length > 0,
          );
          if (filteredIpnList.length > 0) {
            // Only apply filtering if there are shared line items, otherwise keep found IPNs
            commonIpnList = filteredIpnList;
          }
        }

        const matchedASNLineQtyMismatch = mappedReferenceLineItems.length !== noduplicateLineItemList.length;

        /* istanbul ignore next */
        if (noduplicateLineItemList.length === 0) {
          // If no common line items are found, throw a warning metric that the IPNs are not a direct match with the document
          const metric: MetricPair = {
            severity: MetricSeverity.WARNING,
            metricType: isPO ? MetricType.IPN_MISMATCH_PO : MetricType.IPN_MISMATCH_ASN,
          };
          selectedModel.metricsList = selectedModel.metricsList?.concat(metric);
        }

        /* istanbul ignore next */
        if (referenceLineItems.length === 0) {
          // If no line items are found, throw a warning metric that the MPN was not found on the document
          const metric: MetricPair = {
            severity: MetricSeverity.WARNING,
            metricType: isPO ? MetricType.MPN_MISMATCH_ASN : MetricType.MPN_MISMATCH_ASN,
          };
          selectedModel.metricsList = selectedModel.metricsList?.concat(metric);
        }

        const fromTownsendMapping =
          mpnIPNPairingTownsendList.find(
            (townSendModel) => townSendModel?.expectedIPN === noduplicateLineItemList[0]?.expectedIPN,
          ) || null;
        const mpnIPNPairing: Model | null = noduplicateLineItemList.length === 1 ? fromTownsendMapping : null;

        const noMappedIpnsTownsendASN = noduplicateLineItemList.length === 0;
        const shouldThrowWarning =
          (referenceLineItemsNoErrors.length === 0 && mpnToIpnMappingsFromAPI?.associatedIpns?.length > 0) ||
          unExpectedLineItems;

        // If there are no common items and document is a Janus or AWS Owned, treat as an error.
        if (noduplicateLineItemList.length === 0 && isJanusOrAWSOwned) {
          setError(MODEL_ERROR_CODES.model_not_exists_catalogue);
          setSelectedModel(selectedModel);
          return;
        }

        // There are no mapped ipns between townsend and ASN
        // mappedIpnCount starts at -1 to be more easily identified if it is an empty list (0) or not set (-1)
        let mappedIpnCount = -1;
        if (mpnToIpnMappingsFromAPI?.associatedIpns?.length > 0 && commonIpnList.length > 0) {
          const mappedIpns = mpnToIpnMappingsFromAPI.associatedIpns.filter((ipn) => {
            return commonIpnList.find(
              (commonIpn) =>
                ipn.ipn === commonIpn.ipn &&
                (ipn.manufacturer === commonIpn.manufacturer || (!ipn.manufacturer && !commonIpn.manufacturer)),
            );
          });
          mappedIpnCount = mappedIpns.length;

          // If setMPNToIPNMapping is called when there is just one mapped IPN do not apply this.
          // Applying this with only one entry will result in either multiple lines and/or modals not to be present.
          if (mappedIpns.length !== 1) {
            setMPNToIPNMapping({
              ...mpnToIpnMappingsFromAPI,
              associatedIpns: mappedIpns.length > 0 ? mappedIpns : mpnToIpnMappingsFromAPI.associatedIpns,
            });
          }
        }
        if (
          noMappedIpnsTownsendASN &&
          mappedIpnCount > 1 &&
          mpnToIpnMappingsFromAPI?.associatedIpns?.length > 1 &&
          skipReferenceId
        ) {
          return;
        }

        // Apply any tags to the tag list
        const asnMpnIPNMappingsList: Model[] = referenceLineItemsNoErrors.map((ref) => {
          const refLineId = ref?.refereceLineItem?.referenceLineItemId;
          const matchedCommonListItem = noduplicateLineItemList.find((asn) => asn.referenceLineItemId === refLineId);
          return {
            ...ref?.refereceLineItem,
            tagList: ref?.refereceLineItem.tags,
            referenceLineItemStatus: ref?.referenceLineItemStatus,
            mappedIPN: matchedCommonListItem ? matchedCommonListItem?.mappedIPN : null,
          };
        });
        const mappedAsnMappings = noduplicateLineItemList?.length > 0 ? noduplicateLineItemList : asnMpnIPNMappingsList;

        const mpnIpnId = uuidv4();

        // Will we show the ASN IPNs list because there is more than one possible pairing
        // If there is exactly one match, then we will display that line.
        const showAsnIPNs =
          (commonIpnList.length > 1 && asnMpnIPNMappingsList.length > 1) ||
          (asnMpnIPNMappingsList.length > 1 && noduplicateLineItemList?.length !== 1) ||
          (matchedASNLineQtyMismatch && asnMpnIPNMappingsList.length > 1);

        // Are we able to auto select the MPN because there
        const autoSelectMPN =
          (noMappedIpnsTownsendASN &&
            mappedAsnMappings.length < 2 &&
            mpnToIpnMappingsFromAPI?.associatedIpns?.length === 1) ||
          commonIpnList.length === 1;

        const ipnMPNPair = mpnToIpnMappingsFromAPI?.associatedIpns?.filter((mpnIpnPairing1) => {
          return (
            !commonIpnList ||
            commonIpnList.length === 0 ||
            commonIpnList.filter((commonIPNEntry) => {
              const commonIPNIncludesPairingIPN: boolean = commonIPNEntry.ipn?.includes(mpnIpnPairing1?.ipn);
              const manufacturerBlankOrMatches: boolean =
                !mpnIpnPairing1.manufacturer || commonIPNEntry.manufacturer === mpnIpnPairing1.manufacturer;
              return commonIPNIncludesPairingIPN && manufacturerBlankOrMatches;
            }).length !== 0
          );
        })[0];

        if (noMappedIpnsTownsendASN && mpnToIpnMappingsFromAPI?.associatedIpns?.length === 1) {
          selectedModel = {
            ...selectedModel,
            manufacturer: ipnMPNPair?.manufacturer,
            referenceId,
            expectedIPN: ipnMPNPair?.ipn,
            description: ipnMPNPair?.description,
            expectedMaterialDefinitionId: ipnMPNPair?.materialDefintionId,
          };
        }

        // Populate model if there is one and only one line item that could not be paired with a Townsend IPN
        // Keep IPN related information the same as it was in the model before
        if (noMappedIpnsTownsendASN && mappedAsnMappings.length === 1) {
          selectedModel = {
            ...selectedModel,
            ...mappedAsnMappings?.[0],

            // Confirm that IPN related fields are populated
            manufacturer: selectedModel?.manufacturer ?? mappedAsnMappings?.[0]?.manufacturer,
            mappedIPN: selectedModel?.mappedIPN,
            description: selectedModel?.description,
            expectedMaterialDefinitionId:
              selectedModel?.expectedMaterialDefinitionId ?? mappedAsnMappings?.[0].expectedMaterialDefinitionId,
          };
        }

        const ipnMPNPairIncludesExpectedIPN: boolean =
          !!noduplicateLineItemList &&
          !!noduplicateLineItemList[0] &&
          ipnMPNPair?.ipn?.includes(noduplicateLineItemList?.[0].expectedIPN);
        const ipnMPNPairIncludesExpectedIPNDashrolled: boolean =
          !!noduplicateLineItemList &&
          !!noduplicateLineItemList[0] &&
          isDashrollMatch(ipnMPNPair?.ipn, noduplicateLineItemList?.[0]?.expectedIPN);
        if (
          noduplicateLineItemList?.length === 1 &&
          !matchedASNLineQtyMismatch &&
          (ipnMPNPairIncludesExpectedIPN || ipnMPNPairIncludesExpectedIPNDashrolled)
        ) {
          selectedModel = {
            ...selectedModel,
            ...noduplicateLineItemList?.[0],
            manufacturer: ipnMPNPair?.manufacturer,
            description: ipnMPNPair.description,
            expectedMaterialDefinitionId: ipnMPNPair.materialDefintionId,
          };
        }
        let selectedAsns = asnMpnIPNMappingsList;
        const selectedAsnsIC =
          noduplicateLineItemList.length > 0
            ? asnMpnIPNMappingsList.map((asn) => {
                const ipn = mpnToIpnMappingsFromAPI?.associatedIpns.find(
                  (v) => v.ipn === noduplicateLineItemList?.[0]?.expectedIPN,
                );
                return { ...asn, description: ipn?.description };
              })
            : [];
        const mutipleAsnWithSelectedIPN =
          commonIpnList.length === 1 && autoSelectMPN ? selectedAsnsIC?.length > 1 : false;

        if (mutipleAsnWithSelectedIPN && autoSelectMPN) {
          selectedAsns = selectedAsnsIC;
        }

        if (autoSelectMPN) {
          const mappedIpn = selectedModel?.mappedIPN ?? ipnMPNPair?.ipn ?? null;
          let mappedDescription = null;
          if (selectedModel?.description) {
            mappedDescription = selectedModel?.description;
          } else if (ipnMPNPair?.description) {
            mappedDescription = ipnMPNPair.description;
          }
          selectedModel = {
            ...selectedModel,
            mpnIpnId: autoSelectMPN ? mpnIpnId : undefined,
            mappedIPN: mappedIpn,
            manufacturer: ipnMPNPair?.manufacturer,
            description: mappedDescription,
            expectedMaterialDefinitionId: ipnMPNPair?.materialDefintionId,
          };
          selectedAsns.forEach((lineItem) => {
            lineItem.expectedMaterialDefinitionId = ipnMPNPair?.materialDefintionId;
            lineItem.mappedIPN = mappedIpn;
            lineItem.manufacturer = ipnMPNPair?.manufacturer;
          });
        }
        if ((showAsnIPNs && !autoSelectMPN) || mutipleAsnWithSelectedIPN) {
          setAsns(selectedAsns);
          setSelectedModel(selectedModel);
          return;
        }

        const selectedReferenceLineItem = mappedReferenceLineItems?.find(
          (ref: Model) => ref?.expectedIPN === mpnIPNPairing?.expectedIPN,
        );

        if (!selectedReferenceLineItem && isJanusOrAWSOwned) {
          setError(MODEL_ERROR_CODES.model_not_exists_catalogue);
          setSelectedModel(selectedModel);
          return;
        }

        if ((showAsnIPNs && autoSelectMPN) || (!autoSelectMPN && !shouldThrowWarning)) {
          setAsns(selectedAsns);
          setSelectedModel(selectedModel);
          return;
        }

        if (shouldThrowWarning && !skipReferenceId) {
          console.log(
            'shouldThrowWarning',
            shouldThrowWarning,
            referenceLineItemsNoErrors,
            unExpectedLineItems,
            skipReferenceId,
            mpnToIpnMappingsFromAPI,
            variableArgs?.mpn,
          );
          setWarning(true);
          if (commonIpnList.length === 1) {
            setMPNToIPNMapping(undefined);
          }
          setSelectedModel({ ...selectedModel, warning: true });
          return;
        }
        if (skipReferenceId) {
          selectedModel = {
            ...selectedModel,
            referenceId,
            orderType: receiveType,
            siteName,
            receiveReason,
            buildCategory: selectedModel.buildCategory,
          };
          setWarning(false);
          setError(null);
        }

        dispatch({
          type: Types.scanModel,
          payload: { ...selectedModel, mpnIpnId },
        });

        !modelId &&
          (!noMappedIpnsTownsendASN || !selectedReferenceLineItem) &&
          history.push(`/receive/${receiveType}/${referenceId}/model/${selectedModel?.mpnIpnId}`);
        onCompleted && onCompleted();
      } catch (error) {
        setError(MODEL_ERROR_CODES.runtime_client_error);
        paginatedResponse.current = INIT_REFERENCE_LINE_ITEMS_RESPONSE;
        logger.error('useMPNsToReceive: runtime client error', {
          error,
        });
      }
    },
    onError: () => {
      paginatedResponse.current = INIT_REFERENCE_LINE_ITEMS_RESPONSE;
      setError(MODEL_ERROR_CODES.model_not_exists_catalogue);
    },
  });
  return {
    searchModels,
    mpnToIPNMapping,
    setMPNToIPNMapping,
    loading: searchModelsResponse?.loading,
    asns,
    setAsns,
    setWarning,
    setError,
    error,
    warning,
    request: searchModelsResponse?.variables,
    selectedModel,
    resetPaginatedResponse: () => {
      paginatedResponse.current = INIT_REFERENCE_LINE_ITEMS_RESPONSE;
    },
  };
};

export default useMPNsToReceive;
